import React from 'react';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import Tooltip from '@mui/material/Tooltip';

export function PanelSelect({
  label,
  value,
  defaultValue,
  onChange,
  options
}) {
  const rootRef = React.useRef(null);
  const controlled = value !== undefined;
  const [open, setOpen] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(defaultValue);
  const selectedValue = controlled ? value : localValue;
  const selectedOption = (options || []).find(option => option.v === selectedValue);
  const selectOption = option => {
    if (!controlled) setLocalValue(option.v);
    onChange?.({
      target: {
        value: option.v
      }
    });
    setOpen(false);
  };
  React.useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);
  return React.createElement("div", {
    ref: rootRef,
    onKeyDown: event => {
      if (event.key === 'Escape') setOpen(false);
    },
    style: {
      "position": "relative",
      "margin": "0.37037vh 1.481481vh 0"
    }
  }, React.createElement("button", {
    type: "button",
    onClick: () => setOpen(current => !current),
    "aria-haspopup": "listbox",
    "aria-expanded": open,
    style: {
      "position": "relative",
      "display": "flex",
      "flexDirection": "column",
      "alignItems": "flex-start",
      "gap": "0.092593vh",
      "width": "100%",
      "minHeight": "4.259259vh",
      "boxSizing": "border-box",
      "padding": "0.277778vh 2.407407vh 0.277778vh 0.925926vh",
      "background": "rgba(3,10,22,0.6)",
      "border": "0.092593vh solid " + (open ? "oklch(0.62 0.18 42)" : "rgba(184,193,206,0.25)"),
      "borderRadius": "0.648148vh",
      "color": "#e4e9f0",
      "cursor": "pointer",
      "textAlign": "left"
    },
    className: "scapp-full-hover"
  }, React.createElement("span", {
    style: {
      "fontSize": "0.925926vh",
      "color": "#8793a6",
      "lineHeight": "1.1",
      "pointerEvents": "none"
    }
  }, label), React.createElement("span", {
    style: {
      "font": "700 1.296296vh 'IBM Plex Mono',monospace",
      "lineHeight": "1.2",
      "whiteSpace": "nowrap",
      "overflow": "hidden",
      "textOverflow": "ellipsis",
      "maxWidth": "100%"
    }
  }, selectedOption?.l || "—"), React.createElement("span", {
    "aria-hidden": "true",
    style: {
      "position": "absolute",
      "right": "0.416667vw",
      "top": "50%",
      "transform": "translateY(-50%)",
      "fontSize": "0.925926vh",
      "color": "oklch(0.66 0.19 42)",
      "pointerEvents": "none"
    }
  }, "▾")), open ? React.createElement("div", {
    role: "listbox",
    style: {
      "position": "absolute",
      "top": "100%",
      "left": "0",
      "right": "0",
      "zIndex": "71",
      "maxHeight": "27.777778vh",
      "overflowY": "auto",
      "background": "#101c2e",
      "border": "0.092593vh solid rgba(184,193,206,0.18)",
      "borderRadius": "0.740741vh",
      "boxShadow": "0 1.296296vh 3.148148vh rgba(0,0,0,0.55)",
      "padding": "0.37037vh",
      "display": "flex",
      "flexDirection": "column",
      "gap": "0.092593vh",
      "overscrollBehavior": "contain"
    }
  }, (options || []).map(option => {
    const selected = option.v === selectedValue;
    return React.createElement("button", {
      key: option.v,
      type: "button",
      role: "option",
      "aria-selected": selected,
      onClick: () => selectOption(option),
      style: {
        "display": "flex",
        "justifyContent": "space-between",
        "gap": "1.111111vh",
        "padding": "0.555556vh 0.925926vh",
        "border": "0",
        "borderRadius": "0.462963vh",
        "background": selected ? "oklch(0.62 0.18 42 / 0.3)" : "transparent",
        "color": selected ? "#fff" : "#d5dbe4",
        "cursor": "pointer",
        "font": "600 1.203704vh 'IBM Plex Mono',monospace",
        "whiteSpace": "nowrap",
        "textAlign": "left",
        "flex": "none"
      },
      className: "scapp-full-hover"
    }, option.l);
  })) : null);
}

function SettingsItem({
  item: i,
  text
}) {
  const button = React.createElement("button", {
    "onClick": i.onClick,
    "aria-disabled": i.disabled || undefined,
    "tabIndex": i.disabled ? -1 : undefined,
    style: {
      "display": "flex",
      "alignItems": "center",
      "gap": "1.481481vh",
      "height": "4.259259vh",
      "padding": "0 1.666667vh",
      "border": "0",
      "background": i.bg,
      "color": i.color,
      "cursor": i.disabled ? "not-allowed" : "pointer",
      "opacity": i.disabled ? 0.65 : 1,
      "font": i.fw + " 1.388889vh 'IBM Plex Sans',sans-serif",
      "textAlign": "left",
      "width": "100%",
      "borderLeft": "0.15625vw solid " + i.bar
    },
    className: "scapp-full-hover"
  }, React.createElement("svg", {
    "width": "20",
    "height": "20",
    "viewBox": "0 0 24 24",
    "fill": "none",
    "stroke": "currentColor",
    "strokeWidth": "1.8",
    "strokeLinecap": "round",
    "strokeLinejoin": "round",
    style: {
      "flex": "none"
    }
  }, React.createElement("path", {
    "d": i.icon
  }, null)), React.createElement("span", {
    style: {
      "flex": "1"
    }
  }, text(i.label)), React.createElement("span", {
    style: {
      "font": "600 0.925926vh 'IBM Plex Sans',sans-serif",
      "color": i.tagC
    }
  }, text(i.tag)));
  return i.tooltip ? React.createElement(Tooltip, {
    title: i.tooltip,
    placement: "left"
  }, React.createElement("span", {
    style: {
      "display": "block"
    }
  }, button)) : button;
}

/**
 * Renderiza el bloque MainSettings de SCAPP V2.
 *
 * @param {object} props
 * @param {object} props.v - Modelo de presentación y callbacks del panel.
 * @returns {React.ReactNode} Bloque visual sin estado local.
 */
export default function MainSettings({
  v
}) {
  return v.drMain ? React.createElement(React.Fragment, null, React.createElement("div", {
    style: {
      "display": "flex",
      "flexDirection": "column"
    }
  }, v.text("\n    "), React.createElement("div", {
    style: {
      "display": "flex",
      "alignItems": "center",
      "justifyContent": "space-between",
      "padding": "0 0.740741vh 0 1.666667vh",
      "height": "7.037037vh",
      "flex": "none",
      "borderBottom": "0.092593vh solid rgba(46,179,238,0.12)"
    }
  }, v.insightAllowed ? React.createElement("button", {
    "onClick": v.openInsight,
    "aria-label": "ITG Insight",
    style: {
      "display": "flex",
      "alignItems": "center",
      "gap": "1.481481vh",
      "height": "4.259259vh",
      "padding": "0 1.111111vh",
      "border": "0",
      "borderRadius": "0.555556vh",
      "background": "transparent",
      "color": "#e4e9f0",
      "cursor": "pointer",
      "font": "500 1.388889vh 'IBM Plex Sans',sans-serif",
      "textAlign": "left",
      "flex": "1"
    },
    className: "scapp-full-hover"
  }, React.createElement(AutoFixHighRoundedIcon, {
    "fontSize": "small"
  }), v.text("ITG Insight")) : null, React.createElement("button", {
    "onClick": v.closeDrawer,
    "title": "Cerrar",
    style: {
      "width": "1.875vw",
      "height": "3.333333vh",
      "border": "0",
      "background": "transparent",
      "color": "#8793a6",
      "cursor": "pointer",
      "fontSize": "1.851852vh",
      "borderRadius": "0.555556vh"
    },
    className: "scapp-full-hover"
  }, v.text("×"))), v.text("\n    "), React.createElement("button", {
    "onClick": v.openCal,
    style: {
      "display": "flex",
      "alignItems": "center",
      "gap": "1.481481vh",
      "height": "4.814815vh",
      "padding": "0 1.666667vh",
      "border": "0",
      "borderTop": "0.092593vh solid rgba(46,179,238,0.08)",
      "background": "rgba(255,255,255,0.03)",
      "color": "#e4e9f0",
      "cursor": "pointer",
      "textAlign": "left",
      "width": "100%"
    },
    className: "scapp-full-hover"
  }, React.createElement("svg", {
    "width": "20",
    "height": "20",
    "viewBox": "0 0 24 24",
    "fill": "none",
    "stroke": "currentColor",
    "strokeWidth": "1.8",
    "strokeLinecap": "round",
    "strokeLinejoin": "round",
    style: {
      "flex": "none"
    }
  }, React.createElement("path", {
    "d": "M4 6h16v14H4zM4 10h16M8 3v4M16 3v4"
  }, null)), React.createElement("span", {
    style: {
      "display": "flex",
      "flexDirection": "column",
      "gap": "0.185185vh",
      "flex": "1",
      "minWidth": "0"
    }
  }, React.createElement("span", {
    style: {
      "font": "1.388889vh 'IBM Plex Sans',sans-serif"
    }
  }, v.text("Calendario")), React.createElement("span", {
    style: {
      "font": "1.018519vh 'IBM Plex Mono',monospace",
      "color": "#8793a6",
      "whiteSpace": "nowrap",
      "overflow": "hidden",
      "textOverflow": "ellipsis"
    }
  }, v.text(v.rngShort))), React.createElement("span", {
    style: {
      "font": "600 1.018519vh 'IBM Plex Mono',monospace",
      "color": "oklch(0.78 0.14 50)"
    }
  }, v.text(v.rngN + " d")))), v.text("\n    "), React.createElement(PanelSelect, {
    label: v.text("Versión de SCAPP"),
    value: v.dashboardVersion === 'v2' ? 'v2' : 'v1',
    onChange: event => v.changeDashboardVersion(event.target.value),
    options: [{
      v: 'v1',
      l: v.text("Versión 1 · Anterior")
    }, {
      v: 'v2',
      l: v.text("Versión 2")
    }]
  }), v.text("\n    "), (v.drawerSecs || []).map((s, index167) => React.createElement(React.Fragment, {
    key: index167
  }, React.createElement("div", {
    style: {
      "display": "flex",
      "flexDirection": "column",
      "paddingTop": "0.555556vh",
      "borderBottom": "0.092593vh solid rgba(46,179,238,0.08)"
    }
  }, v.text("\n      "), React.createElement("button", {
    "onClick": s.toggle,
    style: {
      "display": "flex",
      "alignItems": "center",
      "justifyContent": "space-between",
      "gap": "0.925926vh",
      "height": "3.703704vh",
      "padding": "0 1.666667vh",
      "border": "0",
      "background": "transparent",
      "cursor": "pointer",
      "font": "600 1.111111vh 'IBM Plex Sans',sans-serif",
      "letterSpacing": "0.08em",
      "textTransform": "uppercase",
      "color": "oklch(0.68 0.17 45)",
      "textAlign": "left",
      "whiteSpace": "nowrap",
      "width": "100%"
    }
  }, React.createElement("span", {
    style: {
      "flex": "1",
      "textAlign": "left"
    }
  }, v.text(s.title)), React.createElement("span", {
    style: {
      "fontSize": "1.018519vh",
      "color": "#8793a6"
    }
  }, v.text(s.chev))), v.text("\n      "), s.open ? React.createElement(React.Fragment, null, React.createElement("div", {
    style: {
      "display": "flex",
      "flexDirection": "column",
      "paddingBottom": "0.740741vh"
    }
  }, (s.groups || []).map((g, index168) => React.createElement(React.Fragment, {
    key: index168
  }, React.createElement("div", {
    style: {
      "display": "flex",
      "flexDirection": "column"
    }
  }, g.hasSub ? React.createElement(React.Fragment, null, React.createElement("div", {
    style: {
      "padding": "0.925926vh 1.666667vh 0.555556vh",
      "font": "600 1.018519vh 'IBM Plex Sans',sans-serif",
      "letterSpacing": "0.08em",
      "textTransform": "uppercase",
      "color": "#b8c1ce",
      "borderTop": "0.092593vh solid rgba(46,179,238,0.08)"
    }
  }, v.text(g.sub))) : null, (g.items || []).map((i, index169) => React.createElement(SettingsItem, {
    key: index169,
    item: i,
    text: v.text
  }))))))) : null, v.text("\n    ")))), v.text("\n    "), React.createElement("div", {
    style: {
      "display": "flex",
      "flexDirection": "column",
      "gap": "1.296296vh",
      "padding": "1.481481vh 0 0"
    }
  }, React.createElement(PanelSelect, {
    "key": v.ayKey,
    label: v.text("Resolución de ayuno"),
    "defaultValue": v.ayVal,
    "onChange": v.onAy,
    options: (v.ayOpts || []).map(o => ({
      v: o.v,
      l: v.text(o.l)
    }))
  }), React.createElement(PanelSelect, {
    "key": v.verKey,
    label: v.text("Ver por"),
    "defaultValue": v.verVal,
    "onChange": v.onVer,
    options: (v.verOpts || []).map(o => ({
      v: o.v,
      l: v.text(o.l)
    }))
  }), v.text("\n    "))) : null;
}
